import { Box, Link, PromptInput } from "@amzn/awsui-components-console";
import React, { useRef, useState } from "react";

import { useChat } from "@/contexts/ChatContext";
import { useUser } from "@/contexts/UserContext";

import ChatContainer from "./ChatContainer";
import { AWS_RESPONSIBLE_AI_POLICY_URL } from "./config";
import { ChatMessage } from "./types";
import { getHumanMessage } from "./utils";

const Chat: React.FC = () => {
  const [prompt, setPrompt] = useState("");
  const { user } = useUser();
  const {
    messages,
    connected,
    previousChatLoading,
    chatLoading,
    sendToChatbot,
    conversationId,
  } = useChat();

  const promptInputRef = useRef<HTMLTextAreaElement>(null);

  const onPromptSend = ({
    detail: { value },
  }: {
    detail: { value: string };
  }) => {
    if (value.length === 0 || !connected || chatLoading) {
      return;
    }

    const newMessage: ChatMessage = getHumanMessage(user, { content: value });
    sendToChatbot(newMessage);
    setPrompt("");
  };

  return (
    <ChatContainer
      messages={messages}
      loading={previousChatLoading}
      conversationId={conversationId}
      promptInput={
        <>
          <PromptInput
            ref={promptInputRef}
            onChange={({ detail }) => setPrompt(detail.value)}
            onAction={onPromptSend}
            value={prompt}
            actionButtonAriaLabel={
              chatLoading ? "Send message button - suppressed" : "Send message"
            }
            actionButtonIconName="send"
            ariaLabel={
              chatLoading ? "Prompt input - suppressed" : "Prompt input"
            }
            placeholder="Ask a question"
            autoFocus
            disabled={chatLoading || !connected || previousChatLoading}
          />
          <Box
            color="text-body-secondary"
            margin={{ top: "xs" }}
            fontSize="body-s"
          >
            Use of this service is subject to the{" "}
            <Link
              href={AWS_RESPONSIBLE_AI_POLICY_URL}
              external
              variant="primary"
              fontSize="inherit"
            >
              AWS Responsible AI Policy
            </Link>
            . You may need to verify responses for accuracy.
          </Box>
        </>
      }
    />
  );
};

export default Chat;
